import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChefHat, Plus, Minus, CheckCircle, XCircle, Utensils, Loader, Clock, Info, X, IceCream, Cookie, Gift } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosPublic from '../hooks/UseAxiosPublic';

const CustomerMenu = () => {
    const { tableNo } = useParams();
    const axiosPublic = UseAxiosPublic();
    
    const [isVerified, setIsVerified] = useState(false);
    const [passcode, setPasscode] = useState('');
    const [tableId, setTableId] = useState(null);
    const [cart, setCart] = useState([]);
    const [isOrderPlaced, setIsOrderPlaced] = useState(false);
    const [activeOrderId, setActiveOrderId] = useState(null); 
    const [orderStatus, setOrderStatus] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null); 
    const [activeTab, setActiveTab] = useState('Ice Cream');
    const [visibleCount, setVisibleCount] = useState(8);

    const categories = [
        { name: 'Ice Cream', icon: <IceCream size={16} /> },
        { name: 'Cookies', icon: <Cookie size={16} /> },
        { name: 'Family Offer', icon: <Gift size={16} /> }
    ];

    // SAFE SOUND FUNCTION
    const playStatusSound = (status) => {
        try {
            const url = status === 'Confirmed' 
                ? 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3' 
                : 'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3';
            const audio = new Audio(url);
            audio.play().catch(() => console.log("Sound play prevented by browser"));
        } catch (err) {
            console.error("Audio error:", err);
        }
    };

    // 1. FETCH MENU
    const { data: menuItems, isLoading } = useQuery({
        queryKey: ['public-menu'],
        queryFn: async () => {
            const res = await axiosPublic.get('products');
            return res.data?.data || [];
        },
        enabled: isVerified
    });

    // 2. POLL ORDER STATUS
    useQuery({
        queryKey: ['order-status', activeOrderId],
        queryFn: async () => {
            if (!activeOrderId) return null;
            const res = await axiosPublic.get(`/table/order/status/${activeOrderId}`);
            const newStatus = res.data.status;
            
            if (orderStatus === 'Pending' && (newStatus === 'Confirmed' || newStatus === 'Cancelled')) {
                playStatusSound(newStatus);
            }
            
            setOrderStatus(newStatus); 
            return newStatus;
        },
        enabled: !!activeOrderId && orderStatus === 'Pending', 
        refetchInterval: 3000 
    });

    // 3. LOGIN HANDLER
    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const res = await axiosPublic.post('/table/verify', { tableNo: Number(tableNo), passcode });
            if (res.data.success) {
                setIsVerified(true);
                setTableId(res.data.tableId);
                Swal.fire({ 
                    toast: true, 
                    position: 'top', 
                    timer: 2000, 
                    showConfirmButton: false, 
                    icon: 'success', 
                    title: `Table ${tableNo} Unlocked` 
                });
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Wrong Passcode' });
        }
    };

    // 4. CART LOGIC
    const addToCart = (item) => {
        const existing = cart.find(c => c.productId === item._id);
        if (existing) {
            setCart(cart.map(c => c.productId === item._id ? { ...c, quantity: c.quantity + 1 } : c));
        } else {
            setCart([...cart, { productId: item._id, productName: item.productName, price: item.sellingPrice, image: item.image, quantity: 1 }]);
        }
    };

    const removeFromCart = (id) => {
        const existing = cart.find(c => c.productId === id);
        if (existing?.quantity === 1) {
            setCart(cart.filter(c => c.productId !== id));
        } else {
            setCart(cart.map(c => c.productId === id ? { ...c, quantity: c.quantity - 1 } : c));
        }
    };

    // 5. PLACE ORDER
    const handlePlaceOrder = async () => {
        if(cart.length === 0) return;
        try {
            const orderData = { 
                tableId, 
                tableNo: Number(tableNo), 
                items: cart.map(item => ({ productId: item.productId, quantity: item.quantity })) 
            };
            const res = await axiosPublic.post('/table/order/place', orderData);
            if(res.data.success) {
                setActiveOrderId(res.data.data._id);
                setOrderStatus('Pending');
                setCart([]);
                setIsOrderPlaced(true);
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Order Failed' });
        }
    };

    // UI FILTERS
    const categoryFiltered = menuItems?.filter(item => item.category === activeTab) || [];
    const displayedItems = categoryFiltered.slice(0, visibleCount);

    // SCREEN: LOCKED
    if (!isVerified) {
        return (
            <div className="min-h-screen bg-[#1a103c] flex flex-col items-center justify-center p-6 text-center">
                <Utensils size={48} className="text-pink-500 mb-6" />
                <h1 className="text-3xl font-black text-white mb-2">Table {tableNo}</h1>
                <form onSubmit={handleLogin} className="w-full max-w-xs space-y-4">
                    <input 
                        type="text" 
                        value={passcode} 
                        onChange={(e) => setPasscode(e.target.value)} 
                        placeholder="Passcode" 
                        className="w-full bg-[#0f0a1f] border border-white/20 rounded-xl py-4 text-white text-center text-xl font-bold" 
                    />
                    <button className="w-full py-4 rounded-xl bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold shadow-lg">
                        Unlock Menu
                    </button>
                </form>
            </div>
        );
    }

    // SCREEN: ORDER STATUS
    if (isOrderPlaced) {
        return (
            <div className="min-h-screen bg-[#1a103c] flex flex-col items-center justify-center p-8 text-center">
                {orderStatus === 'Pending' ? (
                    <>
                        <Clock size={80} className="text-orange-400 mb-6 animate-pulse" />
                        <h2 className="text-3xl font-bold text-white mb-2">Order Sent!</h2>
                        <p className="text-slate-400">Waiting for kitchen confirmation...</p>
                    </>
                ) : orderStatus === 'Cancelled' ? (
                    <>
                        <XCircle size={100} className="text-red-500 mb-6" />
                        <h2 className="text-4xl font-black text-white mb-4">Order Rejected</h2>
                        <p className="text-red-400 font-bold text-lg mb-8">The kitchen cannot fulfill this order right now.</p>
                        <button 
                            onClick={() => { setIsOrderPlaced(false); setActiveOrderId(null); setOrderStatus(null); }} 
                            className="px-8 py-3 rounded-xl bg-white/10 text-white font-bold border border-red-500/50"
                        >
                            Try Again
                        </button>
                    </>
                ) : (
                    <>
                        <CheckCircle size={100} className="text-emerald-500 mb-6" />
                        <h2 className="text-4xl font-black text-white mb-4">Confirmed!</h2>
                        <p className="text-emerald-400 font-bold text-lg mb-8">Your food is being prepared.</p>
                        <button 
                            onClick={() => { setIsOrderPlaced(false); setActiveOrderId(null); setOrderStatus(null); }} 
                            className="px-8 py-3 rounded-xl bg-white/10 text-white font-bold"
                        >
                            Order More
                        </button>
                    </>
                )}
            </div>
        );
    }

    // MAIN MENU UI
    return (
        <div className="min-h-screen bg-[#0f0a1f] pb-32 pt-16">
            <div className="fixed top-0 left-0 right-0 z-20 bg-[#1a103c]/90 backdrop-blur-md border-b border-white/5 p-4 flex justify-between items-center shadow-lg">
                <div className="flex items-center gap-2">
                    <ChefHat className="text-pink-500" size={24} />
                    <h2 className="text-lg font-bold text-white">Ice Cream Bar</h2>
                </div>
                <div className="bg-white/10 px-3 py-1 rounded-full text-xs font-bold text-white">Table {tableNo}</div>
            </div>

            <div className="flex overflow-x-auto gap-2 px-4 py-6 no-scrollbar mt-4">
                {categories.map((tab) => (
                    <button 
                        key={tab.name} 
                        onClick={() => { setActiveTab(tab.name); setVisibleCount(8); }}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all ${
                            activeTab === tab.name ? 'bg-pink-600 text-white' : 'bg-[#1a103c] text-slate-400 border border-white/5'
                        }`}
                    >
                        {tab.icon} {tab.name}
                    </button>
                ))}
            </div>

            <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {displayedItems.map(item => {
                    const inCart = cart.find(c => c.productId === item._id);
                    return (
                        <div key={item._id} className="bg-[#1a103c] rounded-2xl overflow-hidden border border-white/5 flex flex-col group">
                            <div className="h-32 md:h-48 w-full bg-white/5 relative cursor-pointer" onClick={() => setSelectedProduct(item)}>
                                <img src={item.image} alt={item.productName} className="w-full h-full object-cover" />
                                <div className="absolute top-2 right-2 bg-black/60 text-emerald-400 text-xs font-bold px-2 py-1 rounded-lg">${item.sellingPrice}</div>
                            </div>
                            <div className="p-3 flex flex-col flex-1">
                                <h3 className="font-bold text-white text-sm line-clamp-1 mb-3">{item.productName}</h3>
                                <div className="mt-auto">
                                    {inCart ? (
                                        <div className="flex items-center justify-between bg-[#0f0a1f] rounded-xl p-1 border border-white/10">
                                            <button onClick={() => removeFromCart(item._id)} className="w-8 h-8 flex items-center justify-center text-white"><Minus size={14}/></button>
                                            <span className="font-bold text-white text-sm">{inCart.quantity}</span>
                                            <button onClick={() => addToCart(item)} className="w-8 h-8 flex items-center justify-center text-white"><Plus size={14}/></button>
                                        </div>
                                    ) : (
                                        <button onClick={() => addToCart(item)} className="w-full py-2 bg-pink-600 text-white rounded-xl text-xs font-bold shadow-lg">Add</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* LOAD MORE BUTTON */}
            {categoryFiltered.length > visibleCount && (
                <div className="flex justify-center py-8">
                    <button 
                        onClick={() => setVisibleCount(prev => prev + 8)}
                        className="px-8 py-3 bg-[#1a103c] text-white font-bold rounded-xl border border-white/10 hover:bg-pink-600 transition-all"
                    >
                        Load More
                    </button>
                </div>
            )}

            {/* PRODUCT MODAL */}
            {selectedProduct && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedProduct(null)}></div>
                    <div className="relative bg-[#1a103c] w-full max-w-lg rounded-3xl overflow-hidden border border-white/10 shadow-2xl animate-in zoom-in-95 duration-200">
                        <button 
                            onClick={() => setSelectedProduct(null)}
                            className="absolute top-4 right-4 z-10 p-2 bg-black/50 text-white rounded-full backdrop-blur-md"
                        >
                            <X size={20} />
                        </button>
                        
                        <div className="h-64 md:h-80 w-full">
                            <img src={selectedProduct.image} alt={selectedProduct.productName} className="w-full h-full object-cover" />
                        </div>
                        
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-2xl font-bold text-white">{selectedProduct.productName}</h2>
                                <span className="text-2xl font-black text-emerald-400">${selectedProduct.sellingPrice}</span>
                            </div>
                            
                            <p className="text-slate-400 text-sm leading-relaxed mb-8">
                                {selectedProduct.description || "Enjoy our premium selection of fresh and delicious items prepared with the finest ingredients."}
                            </p>
                            
                            <div className="flex gap-4">
                                {cart.find(c => c.productId === selectedProduct._id) ? (
                                    <div className="flex-1 flex items-center justify-between bg-[#0f0a1f] rounded-2xl p-2 border border-white/10">
                                        <button onClick={() => removeFromCart(selectedProduct._id)} className="w-12 h-12 flex items-center justify-center text-white bg-white/5 rounded-xl"><Minus /></button>
                                        <span className="font-bold text-xl text-white">{cart.find(c => c.productId === selectedProduct._id).quantity}</span>
                                        <button onClick={() => addToCart(selectedProduct)} className="w-12 h-12 flex items-center justify-center text-white bg-white/5 rounded-xl"><Plus /></button>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => addToCart(selectedProduct)}
                                        className="flex-1 py-4 bg-pink-600 text-white rounded-2xl font-bold text-lg shadow-lg hover:bg-pink-500 transition-all"
                                    >
                                        Add to Order
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {cart.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 p-4 z-30 bg-gradient-to-t from-[#0f0a1f] to-transparent">
                    <button onClick={handlePlaceOrder} className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-4 rounded-2xl shadow-xl flex justify-between items-center active:scale-95 transition-all">
                        <div className="flex items-center gap-3">
                            <div className="bg-black/20 w-10 h-10 rounded-full flex items-center justify-center font-bold">
                                {cart.reduce((s, i) => s + i.quantity, 0)}
                            </div>
                            <span className="font-bold text-sm">Send Order</span>
                        </div>
                        <span className="font-black text-xl">${cart.reduce((s, i) => s + (i.price * i.quantity), 0).toFixed(2)}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomerMenu;